/**
 * this is lifted from its npm package source codes
 * @ai-media-lib/socket-messages
 */

import { InjectionToken, Injectable, Inject, NgModule, isDevMode } from '@angular/core';
import { Subject, Subscription, timer, EMPTY, Observable, ObservableInput, OperatorFunction, race, throwError } from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import { map, switchAll, tap, filter, switchMap, first, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';


type TIncident = 'connected' | 're-connecting' | 're-connected' | 'no-connection';

class WSError extends Error {
    private _status!: number
    constructor(message = 'Unknown error', status: number) {
        super(message);
        // tslint:disable-next-line:variable-name
        this._status = 500;
        this.name = this.constructor.name;
        if (status) {
            this._status = parseInt(status.toString(), 10);
        }
    }
    getStatus() {
        return this._status;
    }
}

/*
 * - Since this token is for internal purposes only, we would not export
 *   this file outside the library.
 * - Creating the DI token in a separate file helps prevent
 *   circular dependencies.
 */
const WS_CONFIG_TOKEN = new InjectionToken('WS_CONFIG');

const DEFAULT_RECONNECTION_INTERVALS = [1, 2, 5, 10, 30, 60];
// LBP-108: liveness probe interval, comfortably above the default 10s request timeout.
const LIVENESS_PROBE_INTERVAL_MS = 20000;
let messageId = 0;

@Injectable()
class SocketMessagesService {
    private isLoggedIn = false
    private reconnectionIntervals: number[]
    private host: string
    public attempts!: number
    public reconnecting$$: Subject<any>
    public reconnecting$: Observable<any>
    public reconnectionInterval$$: Subject<any>
    public reconnectionInterval$: Observable<any>
    public incidents$$: Subject<any>
    public incidents$: Observable<any>
    public packets$$: Subject<any>
    public messages$$: Subject<any>
    public events$$: Subject<any>
    public events$: Observable<any>
    public socket$$!: Subject<any> | undefined
    public messages$: Observable<any>
    private livenessSub?: Subscription




    constructor(private auth: AuthService,) {
        /**
         * Array of intervals in seconds when reconnection will be attempted.
         * If it is not set in config - DEFAULT_RECONNECTION_INTERVALS will be used
         * @private
         */
        this.reconnectionIntervals = DEFAULT_RECONNECTION_INTERVALS;
        /**
         * Emits value when first reconnection attempts
         * @private
         */
        this.reconnecting$$ = new Subject();
        /**
         * Used to manage reconnection attempts
         * @private
         */
        this.reconnecting$ = this.reconnecting$$.asObservable();
        this.reconnectionInterval$$ = new Subject();
        /**
         * Emits value on every reconnection attempt
         */
        this.reconnectionInterval$ = this.reconnectionInterval$$.asObservable();
        this.attempts = 0;
        this.incidents$$ = new Subject();
        /**
         * List of incidents happened during session
         */
        this.incidents$ = this.incidents$$.asObservable();
        this.packets$$ = new Subject();
        this.messages$$ = new Subject();
        this.events$$ = new Subject();
        /**
         * Emits events when they happened
         */
        this.events$ = this.events$$.pipe(map((_: any) => _.data))
        // if (config.reconnectionInterval) {
        //     this.reconnectionIntervals = config.reconnectionInterval;
        // }
        this.host = environment.wsServer;
        //at first - subscribe to packets
        this.packets$$.asObservable().pipe(switchAll() as OperatorFunction<unknown, unknown>, tap((packet: any) => {
            if (packet.msg !== 'event') {
                this.messages$$.next(packet);
            }
            else {
                this.events$$.next(packet);
            }
        })).subscribe();
        // LBP-360: the code check used to live here, on the shared messages$$ stream, so it ran
        // for every in-flight request's subscription regardless of which packet's id it belonged
        // to - one request's error response threw inside every other concurrently pending
        // request's chain too. It's now scoped per-request inside send() below, after filtering
        // by id.
        this.messages$ = this.messages$$.asObservable();
        this.reconnecting$.pipe(tap(() => {
            if (!this.attempts) {
                // first attempt to re-connect
                this.incidents$$.next({ type: 're-connecting' });
            }
        }), map(() => {
            return this.attempts++;
        }), tap(index => {
            if (this.reconnectionIntervals[index] === undefined) {
                this.reconnecting$$.complete();
            }
            else {
                if (isDevMode()) {
                    console.debug('Trying to reconnect  - next attempt will start in %s second(s)', this.reconnectionIntervals[index]);
                }
            }
        }), filter(index => this.reconnectionIntervals[index] !== undefined), map(index => this.reconnectionIntervals[index]), switchMap(startAfter => timer(startAfter * 1000)), tap(() => this.reconnectionInterval$$.next(this.reconnectionIntervals[this.attempts]))).subscribe({
            next: () => {
                this.connect();
            },
            complete: () => {
                this.incidents$$.next({ type: 'no-connection' });
                if (isDevMode()) {
                    console.debug('Number of reconnection attempts exhausted.');
                }
            }
        });
        //order makes sense
        this.connect();
        this.auth.isLoggedIn$.pipe(
            tap(isLoggedIn => {
                this.isLoggedIn = isLoggedIn
            })
        ).subscribe();
    }
    /**
     * Send a request using web socket, wait for response and send it to user
     * @param msg
     * @param data
     */
    request$(msg?: string, data?: any): Observable<any> {
        const userPayload = !!msg && msg === 'logout' ? data
            : this.auth.getUserInfo() || {}

        const send = (): Observable<any> => {
            if (!this.isConnected()) {
                return throwError(() => new Error('No connection'));
            }
            const id = ++messageId;
            const packet = {
                id,
                msg,
                user: userPayload
            };
            if (data) {
                Object.assign(packet, { data });
            }
            this.socket$$?.next(packet);
            return this.messages$.pipe(
                filter((p: any) => p.id === id),
                tap((p: any) => {
                    if (!p.code || p.code !== 200) {
                        //401 - is unauthenticated
                        //403 - authenticated but has not enough access rights to the resource
                        throw new WSError((p.info) ? p.info : p.error, p.code);
                    }
                }),
                map(_ => _.data),
                first(),
                tap((_) => {
                    if (isDevMode()) {
                        console.debug('Request %o; Response %o', { msg, data }, _)
                    }
                })
            );
        };

        const timeoutMs = environment.messages?.TIMEOUT ?? 10000;

        if (this.isConnected()) {
            // LBP-108: isConnected() can't detect a silently half-open connection, so race the
            // same timeout used below rather than trusting a bare send() to ever resolve.
            return race(
                send(),
                timer(timeoutMs).pipe(
                    switchMap(() => throwError(() => new Error('No connection')))
                )
            );
        }

        return race(
            this.incidents$.pipe(
                filter((i: any) =>
                    i?.type === 'connected' ||
                    i?.type === 're-connected' ||
                    i?.type === 'no-connection'),
                first(),
                switchMap((i: any) =>
                    i?.type === 'no-connection'
                        ? throwError(() => new Error('No connection'))
                        : send())
            ),
            timer(timeoutMs).pipe(
                switchMap(() => throwError(() => new Error('No connection')))
            )
        );
    }
    /**
     * Public method to initiate forced reconnection
     */
    forceConnect() {
        this.attempts = 0;
        if (isDevMode()) {
            console.debug('Reconnection forced');
        }
        this.connect();
    }
    close() {
        if (this.socket$$) {
            this.socket$$.complete();
        }
    }
    isConnected() {
        return !!this.socket$$ && !this.socket$$.closed;
    }
    getNewWebSocket$$() {
        return webSocket({
            url: this.host,
            openObserver: {
                next: () => {
                    this.incidents$$.next({
                        type: (!this.attempts) ? 'connected' : 're-connected'
                    });
                    this.attempts = 0;
                    this.startLivenessCheck();
                    if (isDevMode()) {
                        console.log('[DataService]: connection started');
                    }
                },
                error: () => {
                    if (isDevMode()) {
                        console.log('[DataService]: connection error');
                    }
                }
            },
            closeObserver: {
                next: () => {
                    if (isDevMode()) {
                        console.log('[DataService]: connection closed', arguments);
                    }
                    this.stopLivenessCheck();
                    this.socket$$ = undefined;
                    this.reconnecting$$.next(true);
                }
            },
        });
    }

    // LBP-108: periodically probes an apparently-open connection; a timed-out probe force-closes
    // the socket via close(), feeding the existing reconnection pipeline below.
    private startLivenessCheck(): void {
        this.livenessSub = timer(LIVENESS_PROBE_INTERVAL_MS, LIVENESS_PROBE_INTERVAL_MS).pipe(
            switchMap(() => this.request$('getCurrentVer').pipe(
                catchError(() => {
                    if (isDevMode()) {
                        console.debug('[DataService]: liveness check failed, forcing reconnect');
                    }
                    this.close();
                    return EMPTY;
                })
            ))
        ).subscribe();
    }

    private stopLivenessCheck(): void {
        this.livenessSub?.unsubscribe();
        this.livenessSub = undefined;
    }

    connect() {
        if (!this.socket$$ || this.socket$$.closed) {
            this.socket$$ = this.getNewWebSocket$$();
            const wsObservable$ = this.socket$$.pipe(catchError((e) => {
                //connection error
                this.socket$$ = undefined;
                //throw new WSError(e.message);
                return EMPTY;
            }));
            this.packets$$.next(wsObservable$);
        }
    }
}

export { SocketMessagesService, WSError, TIncident }
